import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Check, CreditCard, Loader2 } from "lucide-react";

const fmtMoney = (n, cur = "SGD") => `${cur === "SGD" ? "S$" : cur + " "}${formatNum(Math.abs(n || 0), { decimals: 2 })}`;

export default function PlanChangeDialog({ open, onOpenChange, me, onApplied }) {
  const [packages, setPackages] = useState([]);
  const [target, setTarget] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [busy, setBusy] = useState(false);

  const currentCode = me?.tier;

  useEffect(() => {
    if (!open) return;
    setTarget(null); setQuote(null);
    api.get("/onboarding/packages").then((r) => setPackages(r.data.packages || [])).catch(() => {});
  }, [open]);

  const preview = useCallback(async (code) => {
    setTarget(code); setQuote(null); setLoadingQuote(true);
    try {
      const { data } = await api.post("/billing/plan/preview", { package_code: code });
      setQuote(data);
    } catch (e) {
      toast.error(apiErr(e));
      setTarget(null);
    } finally { setLoadingQuote(false); }
  }, []);

  const confirm = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/plan/change", {
        package_code: quote.target_package, origin_url: window.location.origin,
      });
      if (data.mode === "checkout" && data.url) {
        window.location.href = data.url;   // upgrade — pay the prorated difference first
        return;
      }
      // downgrade / lateral — applied immediately
      const credit = data.credit_applied_sgd || 0;
      toast.success(credit > 0
        ? `Plan changed. A credit of ${fmtMoney(credit, quote.currency)} will apply to your next invoice.`
        : "Plan changed successfully.");
      onOpenChange(false);
      onApplied?.();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const cur = quote?.currency || me?.currency || "SGD";
  const isUpgrade = quote && quote.net_sgd > 0;
  const isDowngrade = quote && quote.net_sgd < 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="plan-change-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Change your plan</DialogTitle>
          <DialogDescription>
            Upgrades are charged pro-rata for the days left in this cycle and take effect once paid.
            Downgrades apply immediately and credit the unused time to your next invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="plan-options">
          {packages.map((p) => {
            const isCurrent = p.code === currentCode;
            const selected = p.code === target;
            return (
              <button
                key={p.code}
                type="button"
                disabled={isCurrent || busy}
                onClick={() => preview(p.code)}
                data-testid={`plan-option-${p.code}`}
                className={`text-left rounded-lg border p-4 transition-colors disabled:cursor-not-allowed
                  ${selected ? "border-accent ring-1 ring-accent bg-accent/5"
                    : isCurrent ? "border-border bg-muted/40 opacity-70"
                    : "border-border hover:border-accent/60"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold">{p.name}</div>
                  {isCurrent && <Badge variant="outline" className="text-[9px]">Current</Badge>}
                </div>
                <div className="font-mono text-lg mt-1">
                  S${p.price_sgd}<span className="text-[11px] text-muted-foreground">/user/mo</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{p.tagline}</div>
              </button>
            );
          })}
        </div>

        {loadingQuote && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Calculating proration…
          </div>
        )}

        {quote && !loadingQuote && (
          <div className="card-flat p-4 space-y-3" data-testid="plan-quote">
            <div className="flex items-center gap-2 text-sm font-semibold">
              {isUpgrade ? <ArrowUpRight className="h-4 w-4 text-accent" />
                : isDowngrade ? <ArrowDownRight className="h-4 w-4 text-[#00E65B]" />
                : <Check className="h-4 w-4 text-accent" />}
              {quote.current_package_name} → {quote.target_package_name}
              <Badge variant="outline" className="ml-auto text-[9px] capitalize">{quote.direction}</Badge>
            </div>

            <div className="text-xs text-muted-foreground">
              {quote.remaining_days} of {quote.cycle_days} days remaining · {quote.billing_cycle} · {quote.seats} seat(s)
            </div>

            <div className="space-y-1.5 text-sm border-t border-border/50 pt-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Credit for unused time ({quote.current_package_name})</span>
                <span className="font-mono text-[#00E65B]">− {fmtMoney(quote.credit_unused_sgd, cur)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Pro-rated charge ({quote.target_package_name})</span>
                <span className="font-mono">{fmtMoney(quote.charge_new_sgd, cur)}</span></div>
              <div className="flex justify-between font-semibold border-t border-border/50 pt-2 mt-1">
                <span>{isUpgrade ? "Due now" : isDowngrade ? "Credit to next invoice" : "Net"}</span>
                <span className="font-mono text-accent" data-testid="plan-quote-net">
                  {isDowngrade ? "− " : ""}{fmtMoney(quote.net_sgd, cur)}
                </span>
              </div>
            </div>

            <div className="text-[11px] text-muted-foreground">
              {isUpgrade
                ? "You'll be taken to secure Stripe checkout. Your plan switches only after payment succeeds."
                : isDowngrade
                  ? "Your plan switches immediately and the credit above is applied to your next invoice."
                  : "Same price — your plan switches immediately at no extra charge."}
            </div>

            <Button variant="primary" className="w-full" disabled={busy} onClick={confirm} data-testid="plan-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : isUpgrade ? <><CreditCard className="h-4 w-4" /> Pay {fmtMoney(quote.net_sgd, cur)} & switch</>
                : <>Switch to {quote.target_package_name}</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
